// app/javascript/pages/Home/Terms.tsx
import { useEffect, useState } from 'react'
import { Head, usePage, Link, router } from '@inertiajs/react'
import { usePublicPage } from '@/hooks/usePublicPage'
import { LandingNavbar } from '@/components/landing/LandingNavbar'
import { LandingFooter } from '@/components/landing/LandingFooter'
import { KatexRenderer } from '@/components/shared/KatexRenderer'
import {
  ShieldAlert,
  GraduationCap,
  Bot,
  Ban,
  FileText,
  Mail,
  ArrowRight,
  History
} from 'lucide-react'
import type { PageProps } from '@/types'

interface TermsVersionItem {
  id: number
  version_number: string
  title: string
  is_active: boolean
  activated_at?: string
}

interface TermsProps extends PageProps {
  current_terms?: {
    id: number
    version_number: string
    title: string
    summary?: string
    content: string
    is_active: boolean
    activated_at?: string
  } | null
  all_versions: TermsVersionItem[]
}

export default function TermsPage() {
  const { auth, current_terms, all_versions } = usePage<TermsProps>().props
  usePublicPage()

  const [selectedVersion, setSelectedVersion] = useState<string>(current_terms?.version_number || '1.0')

  useEffect(() => {
    document.title = 'Terms of Service — PrepKiya'
  }, [])

  const handleVersionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value
    setSelectedVersion(v)
    router.get('/terms', { version: v }, { preserveScroll: true, preserveState: true })
  }

  return (
    <>
      <Head title="Terms of Service & EULA — PrepKiya">
        <meta
          name="description"
          content="PrepKiya Terms of Service and End-User License Agreement: Educational purpose, AI-generated content disclaimers, usage guidelines, and limitation of liability."
        />
      </Head>

      <div className="min-h-screen w-full flex flex-col bg-white text-slate-900 antialiased selection:bg-[#c8933b]/20 selection:text-slate-900">
        <LandingNavbar user={auth?.user} />

        <main className="w-full flex-1 py-8 sm:py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-3.5 sm:px-6 lg:px-8 space-y-8 sm:space-y-12">

            {/* Header */}
            <div className="text-center space-y-3">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#f4f7fb] text-[#131b2e] border border-slate-200 shadow-2xs">
                <ShieldAlert className="h-3.5 w-3.5 text-[#c8933b]" />
                <span>Legal & Compliance</span>
              </div>
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black text-[#131b2e] tracking-tight">
                Terms of Service & License Agreement
              </h1>
              <p className="text-xs sm:text-sm md:text-base text-slate-600 max-w-2xl mx-auto">
                {current_terms?.activated_at
                  ? `Effective as of ${current_terms.activated_at}`
                  : 'Effective as of September 14, 2026'}. Please read these terms carefully before accessing or using PrepKiya.
              </p>

              {/* Version indicator and selector */}
              <div className="flex items-center justify-center gap-3 pt-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#f8fafc] text-slate-700 text-xs font-semibold border border-slate-200">
                  <FileText className="w-3.5 h-3.5 text-[#c8933b]" />
                  Version {current_terms?.version_number || '1.0'}
                  {current_terms?.is_active && (
                    <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-[#c8933b]/15 text-[#b5802a] font-bold">
                      Current
                    </span>
                  )}
                </span>

                {all_versions && all_versions.length > 1 && (
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <History className="w-3.5 h-3.5" />
                    <select
                      value={selectedVersion}
                      onChange={handleVersionChange}
                      className="bg-white border border-slate-200 rounded-md px-2 py-1 text-xs text-slate-800 focus:outline-hidden focus:ring-1 focus:ring-[#131b2e]"
                    >
                      {all_versions.map((v) => (
                        <option key={v.id} value={v.version_number}>
                          v{v.version_number} {v.is_active ? '(Active)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Principles Strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-5 rounded-2xl border border-slate-200 bg-[#f8fafc] shadow-xs space-y-2">
                <div className="w-9 h-9 rounded-xl bg-[#f4f7fb] border border-slate-200 flex items-center justify-center text-[#131b2e]">
                  <GraduationCap className="h-4 w-4" />
                </div>
                <h2 className="text-sm font-bold text-[#131b2e]">Educational Aid Only</h2>
                <p className="text-xs text-slate-600 leading-relaxed">
                  PrepKiya is solely a practice tool. It does not confer credentials or guarantee examination success or professional certification.
                </p>
              </div>

              <div className="p-5 rounded-2xl border border-slate-200 bg-[#f8fafc] shadow-xs space-y-2">
                <div className="w-9 h-9 rounded-xl bg-[#f4f7fb] border border-slate-200 flex items-center justify-center text-[#131b2e]">
                  <Bot className="h-4 w-4" />
                </div>
                <h2 className="text-sm font-bold text-[#131b2e]">AI-Generated Content</h2>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Content is synthesized from public web information. We offer no warranty regarding accuracy, authority, or source completeness.
                </p>
              </div>

              <div className="p-5 rounded-2xl border border-slate-200 bg-[#f8fafc] shadow-xs space-y-2">
                <div className="w-9 h-9 rounded-xl bg-[#f4f7fb] border border-slate-200 flex items-center justify-center text-[#131b2e]">
                  <Ban className="h-4 w-4" />
                </div>
                <h2 className="text-sm font-bold text-[#131b2e]">Usage Restrictions</h2>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Rebranding, reselling, automated scraping, discrimination, and political or religious promotion are strictly prohibited.
                </p>
              </div>
            </div>

            {/* Full Legal Document Card */}
            <div className="p-6 sm:p-8 rounded-3xl border border-slate-200 bg-white shadow-xs space-y-6">
              {current_terms?.summary && (
                <div className="p-4 rounded-xl bg-[#f8fafc] border border-slate-200 text-xs text-slate-600 space-y-1">
                  <p className="font-bold text-[#131b2e] text-xs uppercase tracking-wider">Document Summary</p>
                  <p>{current_terms.summary}</p>
                </div>
              )}

              {/* Render content */}
              <div className="text-slate-800 leading-relaxed">
                <KatexRenderer
                  content={current_terms?.content || 'No terms content currently available.'}
                  format="markdown"
                  disableExamHelpers
                  className="markdown-content text-xs sm:text-sm leading-relaxed"
                />
              </div>
            </div>

            {/* Footer Support Card */}
            <div className="p-6 sm:p-8 rounded-3xl border border-slate-200 bg-[#f8fafc] flex flex-col sm:flex-row items-center justify-between gap-5">
              <div className="space-y-1 text-center sm:text-left">
                <h3 className="text-base font-bold text-[#131b2e]">Have questions about these terms?</h3>
                <p className="text-xs sm:text-sm text-slate-600">
                  Reach out to our compliance and support team directly.
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <a
                  href="mailto:prepkiya@gmail.com"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-medium bg-[#131b2e] text-white hover:bg-[#1e293b] transition-colors shadow-xs"
                >
                  <Mail className="h-4 w-4" />
                  <span>Email prepkiya@gmail.com</span>
                </a>
                <Link
                  href="/help"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-xs sm:text-sm font-medium border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 transition-colors"
                >
                  <span>Visit Help Center</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

          </div>
        </main>

        <LandingFooter />
      </div>
    </>
  )
}
